/**
 * Domain Configuration
 * Maps domains/subdomains to language and regional settings
 */

import { setDocumentDirection } from './documentDirection';

export interface DomainConfig {
    language: string;
    currency: string;
    region: string;
    title: string;
    description: string;
}

/**
 * Get configuration based on current domain/subdomain
 */
export const getDomainConfig = (): DomainConfig => {
    const hostname = window.location.hostname;

    // Map of domain → configuration
    const domainConfigs: Record<string, DomainConfig> = {
        // English domains
        'p2pexchange.com': {
            language: 'en',
            currency: 'USD',
            region: 'US',
            title: 'P2P Exchange - Currency Exchange Calculator',
            description: 'Real-time currency exchange rates and converter'
        },
        'www.p2pexchange.com': {
            language: 'en',
            currency: 'USD',
            region: 'US',
            title: 'P2P Exchange - Currency Exchange Calculator',
            description: 'Real-time currency exchange rates and converter'
        },
        'en.p2pexchange.com': {
            language: 'en',
            currency: 'USD',
            region: 'US',
            title: 'P2P Exchange - Currency Exchange Calculator',
            description: 'Real-time currency exchange rates and converter'
        },

        // Spanish domains
        'p2pexchange.es': {
            language: 'es',
            currency: 'EUR',
            region: 'ES',
            title: 'P2P Exchange - Calculadora de Cambio de Divisas',
            description: 'Tasas de cambio de divisas en tiempo real y convertidor'
        },
        'es.p2pexchange.com': {
            language: 'es',
            currency: 'EUR',
            region: 'ES',
            title: 'P2P Exchange - Calculadora de Cambio de Divisas',
            description: 'Tasas de cambio de divisas en tiempo real y convertidor'
        },

        // French domains
        'p2pexchange.fr': {
            language: 'fr',
            currency: 'EUR',
            region: 'FR',
            title: 'P2P Exchange - Calculateur de Change de Devises',
            description: 'Taux de change en temps réel et convertisseur'
        },
        'fr.p2pexchange.com': {
            language: 'fr',
            currency: 'EUR',
            region: 'FR',
            title: 'P2P Exchange - Calculateur de Change de Devises',
            description: 'Taux de change en temps réel et convertisseur'
        },

        // Chinese domains
        'p2pexchange.cn': {
            language: 'zh',
            currency: 'CNY',
            region: 'CN',
            title: 'P2P Exchange - 货币兑换计算器',
            description: '实时汇率和货币转换器'
        },
        'zh.p2pexchange.com': {
            language: 'zh',
            currency: 'CNY',
            region: 'CN',
            title: 'P2P Exchange - 货币兑换计算器',
            description: '实时汇率和货币转换器'
        },

        // Farsi/Persian domains
        'p2pexchange.ir': {
            language: 'fa',
            currency: 'IRR',
            region: 'IR',
            title: 'P2P Exchange - ماشین حساب تبدیل ارز',
            description: 'نرخ ارز لحظه‌ای و تبدیل ارز'
        },
        'fa.p2pexchange.com': {
            language: 'fa',
            currency: 'IRR',
            region: 'IR',
            title: 'P2P Exchange - ماشین حساب تبدیل ارز',
            description: 'نرخ ارز لحظه‌ای و تبدیل ارز'
        },

        // Localhost (development)
        'localhost': {
            language: 'en',
            currency: 'USD',
            region: 'US',
            title: 'P2P Exchange - Currency Exchange Calculator (Dev)',
            description: 'Development environment'
        }
    };

    // Return config for current domain, or default to English
    return domainConfigs[hostname] || domainConfigs['p2pexchange.com'];
};

/**
 * Get language from domain/subdomain
 */
export const getLanguageFromDomain = (): string => {
    const hostname = window.location.hostname;

    // Check for exact domain matches
    const exactMatches: Record<string, string> = {
        'p2pexchange.es': 'es',
        'p2pexchange.fr': 'fr',
        'p2pexchange.cn': 'zh',
        'p2pexchange.ir': 'fa'
    };

    if (exactMatches[hostname]) {
        return exactMatches[hostname];
    }

    // Check for subdomain (e.g., es.p2pexchange.com)
    const subdomain = hostname.split('.')[0];
    const subdomainLanguages: Record<string, string> = {
        'es': 'es',
        'fr': 'fr',
        'zh': 'zh',
        'fa': 'fa',
        'en': 'en'
    };

    return subdomainLanguages[subdomain] || 'en';
};

/**
 * Get default currency from domain
 */
export const getDefaultCurrencyFromDomain = (): string => {
    const config = getDomainConfig();
    return config.currency;
};

/**
 * Update page title and meta tags based on domain
 */
export const updateMetaFromDomain = (): void => {
    const config = getDomainConfig();

    // Update title
    document.title = config.title;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
        metaDescription.setAttribute('content', config.description);
    } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = config.description;
        document.head.appendChild(meta);
    }

    // Use centralized direction setting function
    setDocumentDirection(config.language);
};

/**
 * Check if current domain is a production domain
 */
export const isProductionDomain = (): boolean => {
    const hostname = window.location.hostname;
    return !hostname.includes('localhost') &&
        !hostname.includes('127.0.0.1') &&
        !hostname.includes('.vercel.app');
};

/**
 * Get all available language domains
 */
export const getLanguageDomains = (): Array<{ lang: string; url: string; label: string }> => {
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const protocol = window.location.protocol;

    // Determine base domain
    const hostname = window.location.hostname;
    let baseDomain = 'p2pexchange.com';

    // If on subdomain, extract base
    if (hostname.includes('.')) {
        const parts = hostname.split('.');
        if (parts.length >= 2) {
            baseDomain = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
        }
    }

    return [
        {
            lang: 'en',
            url: `${protocol}//${baseDomain}${currentPath}${currentSearch}`,
            label: 'English'
        },
        {
            lang: 'es',
            url: `${protocol}//es.${baseDomain}${currentPath}${currentSearch}`,
            label: 'Español'
        },
        {
            lang: 'fr',
            url: `${protocol}//fr.${baseDomain}${currentPath}${currentSearch}`,
            label: 'Français'
        },
        {
            lang: 'zh',
            url: `${protocol}//zh.${baseDomain}${currentPath}${currentSearch}`,
            label: '中文'
        },
        {
            lang: 'fa',
            url: `${protocol}//fa.${baseDomain}${currentPath}${currentSearch}`,
            label: 'فارسی'
        }
    ];
};

export default {
    getDomainConfig,
    getLanguageFromDomain,
    getDefaultCurrencyFromDomain,
    updateMetaFromDomain,
    isProductionDomain,
    getLanguageDomains
};

